import React, { useState } from "react";

export default function EmailAnalysis() {
  const [emailText, setEmailText] = useState(
    `Subject: Meeting Reminder
Dear Rohit,
Just reminding you about the 7 PM meeting today.
Regards,
Amit`
  );

  const [intent, setIntent] = useState("Summarize");
  const [language, setLanguage] = useState("English");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // Simple masking for email names
  function maskNames(text) {
    const nameRegex = /([A-Z][a-zA-Z]{2,20})/g;
    let counter = 1;
    const map = {};
    return text.replace(nameRegex, (match) => {
      if (!map[match]) map[match] = `Person${counter++}`;
      return map[match];
    });
  }

  const onSend = async () => {
    setLoading(true);
    setResult("");

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chatText: emailText, // backend remains same
          intent,
          language,
        }),
      });

      if (!res.ok) throw new Error("API Error");
      const data = await res.json();
      setResult(data.result || "No result");
    } catch (err) {
      const masked = maskNames(emailText);
      let fallback = "Local fallback: " + masked.substring(0, 80) + "...";
      setResult(fallback);
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    setSaveStatus("Saving...");
    const payload = {
      userId: "demo-user",
      chatOriginal: emailText,
      chatMasked: maskNames(emailText),
      intent,
      language,
      response: result,
    };

    try {
      const res = await fetch("/api/save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) setSaveStatus(`Saved (id: ${data.id})`);
      else setSaveStatus("Save failed");
    } catch (err) {
      setSaveStatus("Save failed");
    }
  };

  const speak = () => {
    const text = result || "No result";
    const u = new SpeechSynthesisUtterance(text);
    if (language.toLowerCase().includes("hindi")) u.lang = "hi-IN";
    else u.lang = "en-US";
    speechSynthesis.cancel();
    speechSynthesis.speak(u);
  };

  return (
    <div className="p-4 mx-auto bg-black text-white rounded-xl shadow-xl space-y-4">
      <h1 className="text-lg font-bold">Email → AI Analysis</h1>

      <div>
        <label className="block text-sm text-slate-400">Paste Email</label>
        <textarea
          className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-sm"
          rows={6}
          value={emailText}
          onChange={(e) => setEmailText(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-slate-400">Intent</label>
          <select
            className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
          >
            <option>Summarize</option>
            <option>Generate Reply</option>
            <option>Extract Action Items</option>
            <option>Tone Analysis</option>
          </select>
        </div>

        <div>
          <label className="block text-sm text-slate-400">Language</label>
          <select
            className="w-full mt-1 p-3 rounded-lg bg-slate-800 border border-slate-700 text-sm"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option>English</option>
            <option>Hindi</option>
            <option>Marathi</option>
            <option>Bengali</option>
          </select>
        </div>
      </div>

      <button
        onClick={onSend}
        className="w-full py-3 bg-blue-500 rounded-lg text-sm font-semibold"
      >
        {loading ? "Processing..." : "Send to AI"}
      </button>

      <div className="bg-slate-800 p-3 rounded-lg">
        <h3 className="text-sm font-semibold mb-1">Result</h3>
        <div className="text-slate-300 text-sm whitespace-pre-wrap">
          {result || "No result yet"}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          className="flex-1 py-3 bg-green-500 rounded-lg text-sm"
          onClick={onSave}
        >
          Save to TCloud
        </button>
        <button
          className="flex-1 py-3 bg-purple-500 rounded-lg text-sm"
          onClick={speak}
        >
          TTS
        </button>
      </div>

      {saveStatus && (
        <div className="text-xs text-slate-400">{saveStatus}</div>
      )}

      <div className="text-xs text-slate-500">This demo uses same backend as chat module.</div>
    </div>
  );
}
